import React, { useEffect, useState } from 'react';
import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { Plus, Trash, X } from 'react-bootstrap-icons';
import { getDirectionOptions } from './MultipleSort';

const createSortRule = (field, direction = 'asc') => ({
  id: `${field}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  field,
  direction
});

const SortOrderModal = ({
  show,
  onHide,
  title = 'Sort Order',
  sortFields = [],
  value = [],
  onApply,
  defaultField,
  defaultDirection = 'asc'
}) => {
  const [draftRules, setDraftRules] = useState([]);

  useEffect(() => {
    if (!show) return;

    if (value.length) {
      setDraftRules(value.map(rule => ({ ...rule })));
      return;
    }

    const initialField = defaultField || sortFields[0]?.value;
    setDraftRules(initialField ? [createSortRule(initialField, defaultDirection)] : []);
  }, [show, value, defaultField, defaultDirection, sortFields]);

  const addRule = () => {
    const usedFields = new Set(draftRules.map(rule => rule.field));
    const nextField = sortFields.find(field => !usedFields.has(field.value))?.value || sortFields[0]?.value;
    if (!nextField) return;

    setDraftRules(prev => [...prev, createSortRule(nextField, defaultDirection)]);
  };

  const updateRule = (id, updates) => {
    setDraftRules(prev => prev.map(rule => (rule.id === id ? { ...rule, ...updates } : rule)));
  };

  const removeRule = id => {
    setDraftRules(prev => prev.filter(rule => rule.id !== id));
  };

  const clearAll = () => {
    const initialField = defaultField || sortFields[0]?.value;
    setDraftRules(initialField ? [createSortRule(initialField, defaultDirection)] : []);
  };

  const handleApply = () => {
    const validRules = draftRules.filter(rule => sortFields.some(field => field.value === rule.field));
    onApply(validRules);
    onHide();
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header className="border-0 pb-0">
        <Modal.Title className="h5 mb-0">{title}</Modal.Title>
        <Button variant="link" className="text-dark p-0 ml-auto" onClick={onHide} aria-label="Close">
          <X size={20} />
        </Button>
      </Modal.Header>
      <Modal.Body className="pt-3">
        {draftRules.length === 0 ? (
          <p className="text-muted mb-0">No sort rules added.</p>
        ) : (
          draftRules.map(rule => {
            const fieldDef = sortFields.find(field => field.value === rule.field) || sortFields[0];
            const directionOptions = getDirectionOptions(fieldDef?.type);

            return (
              <Row key={rule.id} className="align-items-center mb-3">
                <Col xs={12} md={5} className="mb-2 mb-md-0">
                  <Form.Control
                    as="select"
                    size="sm"
                    value={rule.field}
                    onChange={e => {
                      const nextFieldDef = sortFields.find(field => field.value === e.target.value);
                      const nextDirectionOptions = getDirectionOptions(nextFieldDef?.type);
                      updateRule(rule.id, {
                        field: e.target.value,
                        direction: nextDirectionOptions.some(option => option.value === rule.direction)
                          ? rule.direction
                          : nextDirectionOptions[0]?.value || 'asc'
                      });
                    }}
                  >
                    {sortFields.map(field => (
                      <option key={field.value} value={field.value}>
                        {field.label}
                      </option>
                    ))}
                  </Form.Control>
                </Col>
                <Col xs={12} md={6} className="mb-2 mb-md-0">
                  <Form.Control
                    as="select"
                    size="sm"
                    value={rule.direction}
                    onChange={e => updateRule(rule.id, { direction: e.target.value })}
                  >
                    {directionOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Control>
                </Col>
                <Col xs={12} md={1} className="text-md-right">
                  <Button
                    variant="link"
                    className="text-danger p-0"
                    onClick={() => removeRule(rule.id)}
                    disabled={draftRules.length === 1}
                    aria-label="Remove sort rule"
                  >
                    <Trash size={16} />
                  </Button>
                </Col>
              </Row>
            );
          })
        )}

        <Button variant="link" className="p-0 text-primary tinyFont" onClick={addRule}>
          <Plus size={14} className="mr-1" />
          Add sort
        </Button>
      </Modal.Body>
      <Modal.Footer className="border-0 pt-0">
        <Button variant="outline-secondary" size="sm" onClick={clearAll}>
          Clear all
        </Button>
        <Button variant="primary" size="sm" onClick={handleApply}>
          Apply sort
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export { createSortRule };
export default SortOrderModal;
